/**
 * Loan Calculator Widget
 * Customer-facing pre-qualification and loan calculation tool
 */

import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Calculator, TrendingUp, PiggyBank, Calendar, DollarSign, Percent,
    CheckCircle2, AlertCircle, ArrowRight, Info, CreditCard, Loader2
} from "lucide-react";
import { formatCurrency } from "@/lib/lendingCalculations";

interface LoanCalculation {
    principal: number;
    interestRate: number;
    termMonths: number;
    monthlyPayment: number;
    totalInterest: number;
    totalPayment: number;
    apr: number;
}

interface PrequalificationResult {
    eligibility: "eligible" | "not_eligible" | "limited";
    message: string;
    maxAffordablePayment: number;
    interestRate: number;
    debtToIncomeRatio: number;
    loanOptions: {
        term_months: number;
        max_amount: number;
        monthly_payment: number;
        total_repayment: number;
    }[];
}

interface AmortizationRow {
    month: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
}

export const LoanCalculator = () => {
    // Calculator State
    const [amount, setAmount] = useState(10000);
    const [term, setTerm] = useState(12);
    const [rate, setRate] = useState(22);

    // Prequalification State
    const [monthlyIncome, setMonthlyIncome] = useState(8000);
    const [monthlyExpenses, setMonthlyExpenses] = useState(4000);
    const [existingDebt, setExistingDebt] = useState(0);
    const [employmentStatus, setEmploymentStatus] = useState("employed");
    const [creditScore, setCreditScore] = useState(650);
    const [prequalResult, setPrequalResult] = useState<PrequalificationResult | null>(null);
    const [isPrequalifying, setIsPrequalifying] = useState(false);

    // Calculate loan details
    const calculation: LoanCalculation = useMemo(() => {
        const monthlyRate = rate / 12 / 100;
        const monthlyPayment = monthlyRate > 0
            ? amount * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1)
            : amount / term;

        const totalPayment = monthlyPayment * term;
        const totalInterest = totalPayment - amount;
        const originationFee = amount * 0.02;
        const apr = rate + (originationFee / amount * 100 / (term / 12));

        return {
            principal: amount,
            interestRate: rate,
            termMonths: term,
            monthlyPayment: Math.round(monthlyPayment * 100) / 100,
            totalInterest: Math.round(totalInterest * 100) / 100,
            totalPayment: Math.round(totalPayment * 100) / 100,
            apr: Math.round(apr * 100) / 100
        };
    }, [amount, term, rate]);

    // Generate amortization schedule
    const schedule: AmortizationRow[] = useMemo(() => {
        const rows: AmortizationRow[] = [];
        const monthlyRate = rate / 12 / 100;
        let balance = amount;

        for (let month = 1; month <= Math.min(term, 12); month++) {
            const interestPayment = balance * monthlyRate;
            const principalPayment = calculation.monthlyPayment - interestPayment;
            balance = Math.max(0, balance - principalPayment);

            rows.push({
                month,
                payment: calculation.monthlyPayment,
                principal: Math.round(principalPayment * 100) / 100,
                interest: Math.round(interestPayment * 100) / 100,
                balance: Math.round(balance * 100) / 100
            });
        }

        return rows;
    }, [amount, term, rate, calculation.monthlyPayment]);

    // Prequalify (local calculation)
    const handlePrequalify = async () => {
        setIsPrequalifying(true);

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 800));

        const disposable = monthlyIncome - monthlyExpenses - existingDebt;
        const maxPayment = disposable * 0.4;
        const dti = ((monthlyExpenses + existingDebt) / monthlyIncome) * 100;

        // Determine rate based on credit score
        let interestRate: number;
        let multiplier: number;

        if (creditScore >= 750) {
            interestRate = 18;
            multiplier = 8;
        } else if (creditScore >= 700) {
            interestRate = 20;
            multiplier = 6;
        } else if (creditScore >= 650) {
            interestRate = 22;
            multiplier = 4;
        } else if (creditScore >= 550) {
            interestRate = 24;
            multiplier = 2.5;
        } else {
            interestRate = 30;
            multiplier = 1.5;
        }

        if (employmentStatus === "self_employed") multiplier *= 0.8;
        else if (employmentStatus === "unemployed") multiplier *= 0.3;

        const monthlyRate = interestRate / 12 / 100;
        const incomeCap = monthlyIncome * multiplier;

        const options: PrequalificationResult["loanOptions"] = [];

        for (const termOption of [6, 12, 18, 24]) {
            const maxLoan = Math.min(
                maxPayment * ((Math.pow(1 + monthlyRate, termOption) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, termOption))),
                incomeCap
            );

            if (maxLoan > 500) {
                const monthly = maxLoan * (monthlyRate * Math.pow(1 + monthlyRate, termOption)) / (Math.pow(1 + monthlyRate, termOption) - 1);
                options.push({
                    term_months: termOption,
                    max_amount: Math.round(maxLoan),
                    monthly_payment: Math.round(monthly * 100) / 100,
                    total_repayment: Math.round(monthly * termOption * 100) / 100
                });
            }
        }

        let eligibility: PrequalificationResult["eligibility"];
        let message: string;

        if (disposable < 500) {
            eligibility = "not_eligible";
            message = "Insufficient disposable income";
        } else if (creditScore < 450) {
            eligibility = "not_eligible";
            message = "Credit score too low";
        } else if (options.length > 0) {
            eligibility = "eligible";
            message = `You may qualify for up to ${formatCurrency(options[options.length - 1].max_amount, "ZMW")}`;
        } else {
            eligibility = "limited";
            message = "Limited options available";
        }

        setPrequalResult({
            eligibility,
            message,
            maxAffordablePayment: maxPayment,
            interestRate,
            debtToIncomeRatio: Math.round(dti * 10) / 10,
            loanOptions: options
        });

        setIsPrequalifying(false);
    };

    const getScoreColor = (score: number) => {
        if (score >= 750) return "text-green-500";
        if (score >= 700) return "text-green-400";
        if (score >= 650) return "text-yellow-500";
        if (score >= 550) return "text-orange-500";
        return "text-red-500";
    };

    const getScoreLabel = (score: number) => {
        if (score >= 750) return "Excellent";
        if (score >= 700) return "Good";
        if (score >= 650) return "Fair";
        if (score >= 550) return "Poor";
        return "Very Poor";
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                        Loan Calculator
                    </h1>
                    <p className="text-white/60">
                        Calculate your monthly payments and check your eligibility
                    </p>
                </div>

                <Tabs defaultValue="calculator" className="space-y-6">
                    <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-white/10">
                        <TabsTrigger value="calculator" className="data-[state=active]:bg-purple-600">
                            <Calculator className="w-4 h-4 mr-2" />
                            Calculator
                        </TabsTrigger>
                        <TabsTrigger value="prequalify" className="data-[state=active]:bg-purple-600">
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Pre-Qualify
                        </TabsTrigger>
                    </TabsList>

                    {/* Calculator Tab */}
                    <TabsContent value="calculator">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Input Card */}
                            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center gap-2">
                                        <Calculator className="w-5 h-5 text-purple-400" />
                                        Loan Details
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Amount Slider */}
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <Label className="text-white">Loan Amount</Label>
                                            <span className="text-2xl font-bold text-white">
                                                {formatCurrency(amount, "ZMW")}
                                            </span>
                                        </div>
                                        <Slider
                                            value={[amount]}
                                            onValueChange={([val]) => setAmount(val)}
                                            min={1000}
                                            max={100000}
                                            step={500}
                                            className="py-4"
                                        />
                                        <div className="flex justify-between text-xs text-white/60">
                                            <span>K1,000</span>
                                            <span>K100,000</span>
                                        </div>
                                    </div>

                                    {/* Term Slider */}
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <Label className="text-white">Loan Term</Label>
                                            <span className="text-xl font-bold text-white">
                                                {term} months
                                            </span>
                                        </div>
                                        <Slider
                                            value={[term]}
                                            onValueChange={([val]) => setTerm(val)}
                                            min={3}
                                            max={36}
                                            step={1}
                                            className="py-4"
                                        />
                                        <div className="flex justify-between text-xs text-white/60">
                                            <span>3 months</span>
                                            <span>36 months</span>
                                        </div>
                                    </div>

                                    {/* Interest Rate */}
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <Label className="text-white">Interest Rate</Label>
                                            <span className="text-xl font-bold text-white">
                                                {rate}% p.a.
                                            </span>
                                        </div>
                                        <Slider
                                            value={[rate]}
                                            onValueChange={([val]) => setRate(val)}
                                            min={12}
                                            max={36}
                                            step={0.5}
                                            className="py-4"
                                        />
                                        <div className="flex justify-between text-xs text-white/60">
                                            <span>12%</span>
                                            <span>36%</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Results Card */}
                            <Card className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 backdrop-blur-lg border-white/20">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-green-400" />
                                        Your Payment
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Monthly Payment - Big Display */}
                                    <div className="text-center py-6 bg-white/10 rounded-xl">
                                        <p className="text-white/60 text-sm mb-2">Monthly Payment</p>
                                        <p className="text-4xl md:text-5xl font-bold text-white">
                                            {formatCurrency(calculation.monthlyPayment, "ZMW")}
                                        </p>
                                        <p className="text-white/60 text-sm mt-2">
                                            for {term} months
                                        </p>
                                    </div>

                                    {/* Summary Grid */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-white/10 rounded-lg">
                                            <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                                                <DollarSign className="w-4 h-4" />
                                                Principal
                                            </div>
                                            <p className="text-lg font-semibold text-white">
                                                {formatCurrency(calculation.principal, "ZMW")}
                                            </p>
                                        </div>
                                        <div className="p-4 bg-white/10 rounded-lg">
                                            <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                                                <Percent className="w-4 h-4" />
                                                Total Interest
                                            </div>
                                            <p className="text-lg font-semibold text-yellow-400">
                                                {formatCurrency(calculation.totalInterest, "ZMW")}
                                            </p>
                                        </div>
                                        <div className="p-4 bg-white/10 rounded-lg">
                                            <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                                                <PiggyBank className="w-4 h-4" />
                                                Total Repayment
                                            </div>
                                            <p className="text-lg font-semibold text-white">
                                                {formatCurrency(calculation.totalPayment, "ZMW")}
                                            </p>
                                        </div>
                                        <div className="p-4 bg-white/10 rounded-lg">
                                            <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                                                <Info className="w-4 h-4" />
                                                APR
                                            </div>
                                            <p className="text-lg font-semibold text-white">
                                                {calculation.apr}%
                                            </p>
                                        </div>
                                    </div>

                                    <Button className="w-full bg-green-600 hover:bg-green-700" size="lg">
                                        Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Amortization Schedule */}
                        <Card className="mt-6 bg-white/10 backdrop-blur-lg border-white/20">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-blue-400" />
                                    Payment Schedule (First 12 Months)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-white/20">
                                                <th className="text-left text-white/60 py-3 px-2">Month</th>
                                                <th className="text-right text-white/60 py-3 px-2">Payment</th>
                                                <th className="text-right text-white/60 py-3 px-2">Principal</th>
                                                <th className="text-right text-white/60 py-3 px-2">Interest</th>
                                                <th className="text-right text-white/60 py-3 px-2">Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schedule.map((row) => (
                                                <tr key={row.month} className="border-b border-white/10 hover:bg-white/5">
                                                    <td className="text-white py-3 px-2">{row.month}</td>
                                                    <td className="text-white text-right py-3 px-2">{formatCurrency(row.payment, "ZMW")}</td>
                                                    <td className="text-green-400 text-right py-3 px-2">{formatCurrency(row.principal, "ZMW")}</td>
                                                    <td className="text-yellow-400 text-right py-3 px-2">{formatCurrency(row.interest, "ZMW")}</td>
                                                    <td className="text-white text-right py-3 px-2">{formatCurrency(row.balance, "ZMW")}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Pre-Qualify Tab */}
                    <TabsContent value="prequalify">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Input Form */}
                            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                                <CardHeader>
                                    <CardTitle className="text-white">Your Financial Profile</CardTitle>
                                    <CardDescription className="text-white/60">
                                        Enter your details to see how much you may qualify for
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    <div className="space-y-2">
                                        <Label className="text-white">Monthly Income</Label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">K</span>
                                            <Input
                                                type="number"
                                                value={monthlyIncome}
                                                onChange={(e) => setMonthlyIncome(Number(e.target.value))}
                                                className="pl-8 bg-white/10 border-white/20 text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-white">Monthly Expenses</Label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">K</span>
                                            <Input
                                                type="number"
                                                value={monthlyExpenses}
                                                onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                                                className="pl-8 bg-white/10 border-white/20 text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-white">Existing Loan Payments</Label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">K</span>
                                            <Input
                                                type="number"
                                                value={existingDebt}
                                                onChange={(e) => setExistingDebt(Number(e.target.value))}
                                                className="pl-8 bg-white/10 border-white/20 text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-white">Employment Status</Label>
                                        <Select value={employmentStatus} onValueChange={setEmploymentStatus}>
                                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="employed">Employed</SelectItem>
                                                <SelectItem value="self_employed">Self-Employed</SelectItem>
                                                <SelectItem value="business_owner">Business Owner</SelectItem>
                                                <SelectItem value="retired">Retired</SelectItem>
                                                <SelectItem value="unemployed">Unemployed</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <Label className="text-white">Credit Score</Label>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xl font-bold ${getScoreColor(creditScore)}`}>
                                                    {creditScore}
                                                </span>
                                                <Badge className={`${getScoreColor(creditScore)} bg-transparent border`}>
                                                    {getScoreLabel(creditScore)}
                                                </Badge>
                                            </div>
                                        </div>
                                        <Slider
                                            value={[creditScore]}
                                            onValueChange={([val]) => setCreditScore(val)}
                                            min={300}
                                            max={850}
                                            step={10}
                                            className="py-4"
                                        />
                                        <div className="flex justify-between text-xs text-white/60">
                                            <span>300</span>
                                            <span>850</span>
                                        </div>
                                    </div>
                                </CardContent>
                                <CardFooter>
                                    <Button
                                        className="w-full bg-purple-600 hover:bg-purple-700"
                                        size="lg"
                                        onClick={handlePrequalify}
                                        disabled={isPrequalifying}
                                    >
                                        {isPrequalifying ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <CheckCircle2 className="w-4 h-4 mr-2" />
                                        )}
                                        Check Eligibility
                                    </Button>
                                </CardFooter>
                            </Card>

                            {/* Results */}
                            <Card className={`backdrop-blur-lg border-white/20 ${prequalResult?.eligibility === "eligible"
                                    ? "bg-gradient-to-br from-green-600/30 to-emerald-600/30"
                                    : prequalResult?.eligibility === "not_eligible"
                                        ? "bg-gradient-to-br from-red-600/30 to-orange-600/30"
                                        : "bg-white/10"
                                }`}>
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center gap-2">
                                        {prequalResult?.eligibility === "eligible" ? (
                                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                                        ) : prequalResult?.eligibility === "not_eligible" ? (
                                            <AlertCircle className="w-5 h-5 text-red-400" />
                                        ) : (
                                            <Info className="w-5 h-5 text-blue-400" />
                                        )}
                                        Pre-Qualification Result
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {!prequalResult ? (
                                        <div className="text-center py-12 text-white/60">
                                            <CreditCard className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                            <p>Fill in your details and click "Check Eligibility"</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-6">
                                            {/* Status Message */}
                                            <div className="text-center py-4">
                                                <p className={`text-lg font-medium ${prequalResult.eligibility === "eligible" ? "text-green-400" :
                                                        prequalResult.eligibility === "not_eligible" ? "text-red-400" : "text-yellow-400"
                                                    }`}>
                                                    {prequalResult.message}
                                                </p>
                                            </div>

                                            {/* Metrics */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="p-4 bg-white/10 rounded-lg">
                                                    <p className="text-white/60 text-sm">DTI Ratio</p>
                                                    <p className="text-xl font-bold text-white">
                                                        {prequalResult.debtToIncomeRatio}%
                                                    </p>
                                                    <Progress value={Math.min(100, prequalResult.debtToIncomeRatio)} className="h-1 mt-2" />
                                                </div>
                                                <div className="p-4 bg-white/10 rounded-lg">
                                                    <p className="text-white/60 text-sm">Interest Rate</p>
                                                    <p className="text-xl font-bold text-white">
                                                        {prequalResult.interestRate}%
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Loan Options */}
                                            {prequalResult.loanOptions.length > 0 && (
                                                <div className="space-y-3">
                                                    <p className="text-white font-medium">Available Options:</p>
                                                    {prequalResult.loanOptions.map((option) => (
                                                        <div
                                                            key={option.term_months}
                                                            className="p-4 bg-white/10 rounded-lg flex justify-between items-center"
                                                        >
                                                            <div>
                                                                <p className="text-white font-medium">
                                                                    {option.term_months} Month Loan
                                                                </p>
                                                                <p className="text-white/60 text-sm">
                                                                    {formatCurrency(option.monthly_payment, "ZMW")}/month
                                                                </p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-green-400 font-bold">
                                                                    {formatCurrency(option.max_amount, "ZMW")}
                                                                </p>
                                                                <p className="text-white/60 text-xs">
                                                                    max amount
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {prequalResult.eligibility === "eligible" && (
                                                <Button className="w-full bg-green-600 hover:bg-green-700" size="lg">
                                                    Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default LoanCalculator;
